/* -------------------------------------------------------------------------- */
/* Line-draw animation (kept on ScrollTrigger — this one is genuinely scroll- */
/* scrubbed, not a one-shot reveal, so it needs continuous scroll position).  */
/* -------------------------------------------------------------------------- */
function initLineAnimation() {
  if (!window.gsap || !window.ScrollTrigger) return;

  gsap.registerPlugin(ScrollTrigger);

  // ignoreMobileResize stops ScrollTrigger from recalculating every time the
  // mobile browser's address bar shows/hides during scroll — a major source
  // of scroll jank on iOS/Android that has nothing to do with your animations.
  if (!initLineAnimation._configured) {
    initLineAnimation._configured = true;
    ScrollTrigger.config({ ignoreMobileResize: true });
  }

  document.querySelectorAll('[data-line-wrap]').forEach(function (wrap) {

    if (wrap.dataset.lineInit) return;
    wrap.dataset.lineInit = "true";

    const line = wrap.querySelector('[data-line]');
    if (!line) return;

    gsap.set(line, {
      scaleY: 0,
      transformOrigin: "top center",
      force3D: true
    });

    gsap.to(line, {
      scaleY: 1,
      ease: "none",
      force3D: true,
      scrollTrigger: {
        trigger: wrap,
        start: "top 85%",
        end: "top 40%",
        scrub: true
      }
    });

  });
}

document.addEventListener('DOMContentLoaded', initLineAnimation);
document.addEventListener('shopify:section:load', initLineAnimation);

/* -------------------------------------------------------------------------- */
/* Global scroll animations                                                    */
/*                                                                             */
/* Headings: every h2 inside a `.custom_section`, plus any `[data-heading]`      */
/* element anywhere, rises into place word by word. The heading is split on      */
/* text nodes only, so <br>, <em>, links etc. survive untouched, and the         */
/* original markup is put back once the animation ends.                          */
/*                                                                              */
/* Fade in: any `[data-fade]` element fades up. Options:                         */
/*   data-fade="up|down|left|right|none"  direction (default: up)                */
/*   data-fade="zoom"                    fade + settle from a 1.06 scale        */
/*   data-fade="grow"                    scale a rule/line out of its origin    */
/*   data-fade-delay="0.2"                delay in seconds                       */
/*   data-fade-distance="40"              travel in px (default: 24)             */
/*   data-fade-group                      on a parent: its [data-fade] children  */
/*                                        animate together as one stagger        */
/*   data-fade-stagger="0.08"             on the group, to tune that stagger     */
/*                                                                              */
/* Opt out with `data-no-anim` on the element or any ancestor. Sections needing  */
/* something bespoke (hero banner, scroll word-fill, Swiper-driven reveals) keep */
/* their own timeline in the section file and opt out here.                      */
/*                                                                              */
/* PERFORMANCE NOTE: this used to give every [data-fade]/heading element its    */
/* own ScrollTrigger — each one doing scroll-position math on every scroll      */
/* frame. With more than a handful of elements on a page that adds up to real   */
/* jank on both desktop and mobile. These are all "reveal once" animations, not */
/* scroll-scrubbed ones, so they don't need ScrollTrigger's per-frame tracking  */
/* at all. They now share a single IntersectionObserver instead — native,      */
/* off-main-thread, and it fires based on actual on-screen visibility rather    */
/* than a scroll-distance formula. That also fixes the "last element never      */
/* reveals" issue for good: a short trailing element (e.g. a footer wordmark)   */
/* still intersects the viewport when the page hits max scroll, even though a   */
/* scroll-position trigger for it might sit past the scrollable range.          */
/*                                                                              */
/* Note: don't put `data-fade` on an element that already has a resting CSS      */
/* transform (e.g. translateX(-50%)) -- GSAP would rewrite it in px. Wrap it or  */
/* fade a parent instead.                                                        */
/* -------------------------------------------------------------------------- */
(function () {
  var HEADING_SELECTOR = '.custom_section h2, [data-heading]';
  var EASE = 'power3.out';
  // Roughly mirrors the old "top 88%" ScrollTrigger start (fire a bit before
  // the element is fully in view). Tune the -12% if you want it earlier/later.
  var ROOT_MARGIN = '0px 0px -12% 0px';

  // Original heading markup, so it can be restored with zero residue.
  var originalMarkup = new WeakMap();
  // Per-element animation config, prepared up front and run on intersection.
  var fadeState = new WeakMap();
  var headingState = new WeakMap();
  var groupState = new WeakMap();

  function motionAllowed() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return document.documentElement.classList.contains('hc-anim');
  }

  // Turns the CSS start states off. Anything this script hides from here on is
  // hidden by inline GSAP styles instead, which it always animates back.
  function handOver() {
    document.documentElement.classList.add('hc-anim-ready');
  }

  function eligible(el) {
    if (!el || el.dataset.animInit === 'true') return false;
    if (el.classList.contains('visually-hidden')) return false;
    if (el.closest('[data-no-anim]')) return false;
    // Swiper's loop clones are copied after init; animating them can leave a
    // clone stuck mid-state.
    if (el.closest('.swiper-slide-duplicate')) return false;
    // No layout box (a display:none breakpoint variant, a collapsed panel):
    // it can never be scrolled into view, so leave it in its resting state
    // rather than hiding something that may never be animated back.
    if (!el.getClientRects().length) return false;
    return true;
  }

  function restingOpacity(el) {
    var value = parseFloat(window.getComputedStyle(el).opacity);
    return isNaN(value) ? 1 : value;
  }

  /* ---------------------------------- Headings --------------------------- */

  // Wraps each word of every text node in a span, leaving element nodes (<br>,
  // <em>, <a>, ...) exactly where they were.
  function splitWords(el) {
    var words = [];

    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 3) {
          if (!child.nodeValue.trim()) return;
          var frag = document.createDocumentFragment();
          child.nodeValue.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part));
              return;
            }
            var span = document.createElement('span');
            span.className = 'anim-word';
            span.textContent = part;
            frag.appendChild(span);
            words.push(span);
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') {
          walk(child);
        }
      });
    })(el);

    return words;
  }

  function restoreHeading(el) {
    var html = originalMarkup.get(el);
    if (html != null) el.innerHTML = html;
    gsap.set(el, { clearProps: 'all' });
  }

  // Sets the hidden start state immediately (same tick as handOver), so the
  // heading never flashes visible before its reveal.
  function prepareHeading(el) {
    originalMarkup.set(el, el.innerHTML);

    var words = splitWords(el);
    if (!words.length) {
      restoreHeading(el);
      return;
    }

    gsap.set(el, { opacity: restingOpacity(el) });
    gsap.set(words, { opacity: 0, y: 30 });
    headingState.set(el, words);
  }

  function runHeading(el) {
    var words = headingState.get(el);
    if (!words) return;

    gsap.to(words, {
      opacity: 1,
      y: 0,
      duration: 0.85,
      ease: EASE,
      stagger: 0.05,
      force3D: true,
      onComplete: function () {
        restoreHeading(el);
      }
    });
  }

  /* ----------------------------------- Fades ----------------------------- */

  function fadeMode(el) {
    return (el.getAttribute('data-fade') || 'up').toLowerCase();
  }

  function fadeFrom(el) {
    var mode = fadeMode(el);
    var distance = parseFloat(el.getAttribute('data-fade-distance'));
    if (isNaN(distance)) distance = 24;

    // A line that grows out of its own transform-origin: no fade, just scale.
    if (mode === 'grow') {
      return { scaleX: 0, transformOrigin: window.getComputedStyle(el).transformOrigin };
    }

    var from = { opacity: 0 };
    if (mode === 'zoom') from.scale = 1.06;
    else if (mode === 'up') from.y = distance;
    else if (mode === 'down') from.y = -distance;
    else if (mode === 'left') from.x = distance;
    else if (mode === 'right') from.x = -distance;

    return from;
  }

  function fadeTo(el) {
    var mode = fadeMode(el);

    var to = {
      duration: mode === 'zoom' ? 1.2 : 0.9,
      ease: EASE,
      delay: parseFloat(el.getAttribute('data-fade-delay')) || 0,
      force3D: true,
      clearProps: 'all'
    };

    if (mode === 'grow') {
      to.scaleX = 1;
      to.duration = 0.8;
      return to;
    }

    to.opacity = restingOpacity(el);
    to.x = 0;
    to.y = 0;
    if (mode === 'zoom') to.scale = 1;

    return to;
  }

  // Applies the hidden start state immediately and stashes the "reveal" tween
  // config for later — the actual tween only runs once IntersectionObserver
  // says the element is on screen.
  function prepareFade(el) {
    el.dataset.animInit = 'true';
    var to = fadeTo(el);
    gsap.set(el, fadeFrom(el));
    // Hints the browser to promote this to its own compositor layer before
    // the animation starts, instead of during the first animated frame.
    el.style.willChange = 'transform, opacity';
    fadeState.set(el, to);
  }

  function runFade(el) {
    var to = fadeState.get(el);
    if (!to) return;
    var onComplete = to.onComplete;
    to.onComplete = function () {
      el.style.willChange = '';
      if (onComplete) onComplete();
    };
    gsap.to(el, to);
  }

  function prepareGroup(group) {
    var children = Array.prototype.filter.call(group.querySelectorAll('[data-fade]'), eligible);
    children.forEach(prepareFade);
    groupState.set(group, children);
  }

  function runGroup(group) {
    var children = groupState.get(group);
    if (!children || !children.length) return;

    var stagger = parseFloat(group.getAttribute('data-fade-stagger'));
    if (isNaN(stagger)) stagger = 0.1;

    // One timeline for the whole group, but every child keeps its own
    // direction and resting opacity.
    var tl = gsap.timeline();

    children.forEach(function (child, index) {
      var to = fadeState.get(child);
      if (!to) return;
      var at = index * stagger + to.delay;
      to.delay = 0;
      var onComplete = to.onComplete;
      to.onComplete = function () {
        child.style.willChange = '';
        if (onComplete) onComplete();
      };
      tl.to(child, to, at);
    });
  }

  /* ------------------------------- Observer ------------------------------ */

  var io = null;

  function getObserver() {
    if (io) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);

        var el = entry.target;
        var kind = el.dataset.animKind;
        if (kind === 'group') runGroup(el);
        else if (kind === 'heading') runHeading(el);
        else runFade(el);
      });
    }, { root: null, rootMargin: ROOT_MARGIN, threshold: 0 });
    return io;
  }

  function observe(el, kind) {
    el.dataset.animKind = kind;
    getObserver().observe(el);
  }

  /* ------------------------------------ Init ----------------------------- */

  function initGlobalAnimations(scope) {
    if (!motionAllowed()) {
      handOver();
      return;
    }

    // No GSAP, or no IntersectionObserver (ancient browser): show everything,
    // animate nothing.
    if (!window.gsap || !window.IntersectionObserver) {
      handOver();
      return;
    }

    var root = scope && scope.querySelectorAll ? scope : document;

    root.querySelectorAll('[data-fade-group]').forEach(function (group) {
      if (group.dataset.animGroupInit === 'true') return;
      group.dataset.animGroupInit = 'true';
      prepareGroup(group);
      observe(group, 'group');
    });

    root.querySelectorAll('[data-fade]').forEach(function (el) {
      if (!eligible(el)) return;
      if (el.closest('[data-fade-group]')) return; // handled by its group
      prepareFade(el);
      observe(el, 'fade');
    });

    root.querySelectorAll(HEADING_SELECTOR).forEach(function (el) {
      if (!eligible(el)) return;
      // A heading inside a fading block travels with that block instead.
      if (el.closest('[data-fade]')) return;
      el.dataset.animInit = 'true';
      prepareHeading(el);
      observe(el, 'heading');
    });

    handOver();
  }

  // For markup that arrives after load (fetched recommendations, re-rendered
  // facet results): call this in the same tick you insert it and it animates in
  // like the rest. Anything already handled is skipped.
  window.HCAnimations = { init: initGlobalAnimations };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initGlobalAnimations(); });
  } else {
    initGlobalAnimations();
  }

  document.addEventListener('shopify:section:load', function (e) { initGlobalAnimations(e.target); });
})();

/* -------------------------------------------------------------------------- */
/* Product card: color swatches, wishlist toggle & quick add-to-cart          */
/* -------------------------------------------------------------------------- */
function formatCardMoney(cents) {
  if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    return Shopify.formatMoney(cents, window.theme && window.theme.moneyFormat ? window.theme.moneyFormat : '${{amount}}');
  }
  return '₹' + (cents / 100).toFixed(2);
}

function initProductCards(container) {
  var scope = container || document;

  scope.querySelectorAll('[data-product-card]').forEach(function (card) {
    if (card.dataset.cardInit === 'true') return;
    card.dataset.cardInit = 'true';

    var variantScript = card.querySelector('[data-variant-json]');
    var variants = variantScript ? JSON.parse(variantScript.textContent) : [];

    function updateCardForVariant(variant) {
      if (!variant) return;

      var quickAdd = card.querySelector('[data-quick-add]');
      if (quickAdd) {
        quickAdd.dataset.variantId = variant.id;
        var label = quickAdd.querySelector('[data-quick-add-label]');
        if (variant.available) {
          quickAdd.disabled = false;
          if (label) label.textContent = 'Add to Cart';
        } else {
          quickAdd.disabled = true;
          if (label) label.textContent = 'Sold Out';
        }
      }

      var priceEl = card.querySelector('[data-price]');
      var compareEl = card.querySelector('[data-compare-price]');
      var discountEl = card.querySelector('[data-discount]');

      if (priceEl) priceEl.textContent = formatCardMoney(variant.price);

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (compareEl) {
          compareEl.textContent = formatCardMoney(variant.compare_at_price);
          compareEl.style.display = '';
        }
        if (discountEl) {
          var pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          discountEl.textContent = '(-' + pct + '%OFF)';
          discountEl.style.display = '';
        }
      } else {
        if (compareEl) compareEl.style.display = 'none';
        if (discountEl) discountEl.style.display = 'none';
      }

      if (variant.featured_image && variant.featured_image.src) {
        var img = card.querySelector('[data-card-image]');
        if (img) img.src = variant.featured_image.src.replace(/(\.[a-zA-Z]{3,4})(\?|$)/, '_600x$1$2');
      }
    }

    /* Wishlist toggle */
    card.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        btn.classList.toggle('is-active');
      });
    });

    /* Color swatches */
    card.querySelectorAll('[data-swatch]').forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        card.querySelectorAll('[data-swatch]').forEach(function (s) { s.classList.remove('is-active'); });
        swatch.classList.add('is-active');

        var variant = variants.find(function (v) { return String(v.id) === String(swatch.dataset.variantId); });
        updateCardForVariant(variant);
      });
    });

    /* Quick add to cart + open cart drawer */
    card.querySelectorAll('[data-quick-add]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        /* Cards for products with real options open the quick-add modal
           instead (see snippets/product-card.liquid). That click belongs to
           Dawn's <modal-opener>, so bail before touching the event -- calling
           preventDefault here would leave the popup dead. */
        if (btn.closest('modal-opener')) return;

        e.preventDefault();
        e.stopPropagation();

        if (btn.disabled) return;

        var variantId = btn.dataset.variantId;
        if (!variantId) return;

        // Use the theme's own cart component (cart-notification or cart-drawer)
        // so add-to-cart matches native behaviour and section rendering.
        var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        btn.disabled = true;
        btn.classList.add('is-loading');

        // Reuse the theme helper so headers/method stay in sync with the theme.
        var config = typeof fetchConfig === 'function'
          ? fetchConfig('javascript')
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };

        var payload = { items: [{ id: variantId, quantity: 1 }] };
        if (cart && cart.getSectionsToRender) {
          payload.sections = cart.getSectionsToRender().map(function (s) { return s.id; });
          payload.sections_url = window.location.pathname;
          if (cart.setActiveElement) cart.setActiveElement(btn);
        }
        config.body = JSON.stringify(payload);

        var addUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add.js';

        fetch(addUrl, config)
          .then(function (res) { return res.json(); })
          .then(function (response) {
            if (response.status) {
              console.error(response.description || 'Could not add to cart');
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-card',
                  productVariantId: variantId,
                  errors: response.errors || response.description,
                  message: response.message
                });
              }
              return;
            }

            // Notify theme subscribers (cart count, upsells, etc.).
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-card',
                productVariantId: variantId,
                cartData: response
              });
            }

            if (cart && cart.renderContents) {
              cart.renderContents(response);
            } else {
              window.location.href = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
            }
          })
          .catch(function (err) {
            console.error(err);
          })
          .finally(function () {
            btn.disabled = false;
            btn.classList.remove('is-loading');
            // Match theme behaviour: clear the empty state on the cart element
            // so the first item added actually shows in the drawer.
            if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          });
      });
    });
  });
}

document.addEventListener('DOMContentLoaded', function () { initProductCards(); });
document.addEventListener('shopify:section:load', function (e) { initProductCards(e.target); });


document.addEventListener("DOMContentLoaded", function () {
  const header = document.querySelector(".section-header");

  if (!header) return;

  // Throttled with requestAnimationFrame + a passive listener, so this never
  // blocks the scroll thread. It was a plain synchronous scroll handler
  // before, which is cheap on its own but adds up when it's running on every
  // scroll pixel alongside everything else on the page.
  var ticking = false;

  function toggleHeaderClass() {
    header.classList.toggle("is-transparent", window.scrollY < 200);
    ticking = false;
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(toggleHeaderClass);
  }

  // Run on page load
  toggleHeaderClass();

  // Run on scroll
  window.addEventListener("scroll", onScroll, { passive: true });
});



/* -------------------------------------------------------------------------- */
/* Filter drawer: turn the nested "inner drawer" into an inline accordion,     */
/* and rebuild the Sort <select> as a radio accordion pinned at the top.       */
/* -------------------------------------------------------------------------- */
(function () {
  var PLUS_SVG =
    '<svg class="hc-plus" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M4.58398 11H17.4173" stroke="black" stroke-width="1.375" stroke-linejoin="round"/>' +
    '<path d="M11 4.58264V17.416" stroke="black" stroke-width="1.375" stroke-linejoin="round"/></svg>';

  // Stop Dawn's MenuDrawer from turning the filter into a slide-in submenu.
  // A capture-phase listener runs before MenuDrawer's bubble listener on the
  // same <summary>; stopImmediatePropagation kills it while the native
  // <details> toggle (the default action) still fires -> plain accordion.
  function blockMenuDrawer(summary) {
    if (summary.dataset.hcAccBound === '1') return;
    summary.dataset.hcAccBound = '1';
    summary.addEventListener('click', function (e) {
      e.stopImmediatePropagation();
    }, true);
  }

  function getSortSelect(wrap) {
    return wrap.querySelector('[id^="Details-Mobile-SortBy"] select[name="sort_by"]');
  }

  function buildSort(wrap) {
    var main = wrap.querySelector('.mobile-facets__main');
    var select = getSortSelect(wrap);
    if (!main || !select) return;

    var original = wrap.querySelector('[id^="Details-Mobile-SortBy"]');
    if (original) original.style.setProperty('display', 'none', 'important');

    var acc = main.querySelector('.hc-sort');
    if (!acc) {
      acc = document.createElement('div');
      acc.className = 'hc-sort mobile-facets__details';
      acc.innerHTML =
        '<button type="button" class="hc-acc__head" aria-expanded="false">' +
          '<div><span class="hc-acc__head-label">Sort by</span>' + PLUS_SVG + '</div>' +
        '</button>' +
        '<div class="hc-acc__panel" hidden><ul class="mobile-facets__list"></ul></div>';
      main.insertBefore(acc, main.firstChild);

      var head = acc.querySelector('.hc-acc__head');
      var panel = acc.querySelector('.hc-acc__panel');
      head.addEventListener('click', function () {
        var open = acc.classList.toggle('is-open');
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.hidden = !open;
      });
      // Radio -> drive the real (hidden) select so facets.js re-filters.
      acc.querySelector('.mobile-facets__list').addEventListener('change', function (e) {
        var radio = e.target.closest('input[type="radio"]');
        if (!radio) return;
        var sel = getSortSelect(wrap);
        if (!sel) return;
        sel.value = radio.value;
        sel.dispatchEvent(new Event('input', { bubbles: true }));
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    // (Re)build the radios only when the options or selection actually change.
    var options = Array.prototype.map.call(select.options, function (o) {
      return o.value + '::' + o.text;
    });
    var signature = options.join('||') + '##' + select.value;
    var list = acc.querySelector('.mobile-facets__list');
    if (list.dataset.sig === signature) return;
    list.dataset.sig = signature;

    list.innerHTML = Array.prototype.map.call(select.options, function (o, i) {
      var id = 'hc-sort-' + i;
      var checked = o.value === select.value ? ' checked' : '';
      return (
        '<li class="mobile-facets__item">' +
          '<label class="hc-sort__label" for="' + id + '">' +
            '<input type="radio" id="' + id + '" name="hc_sort_ui" form="hc-noform" value="' +
              o.value.replace(/"/g, '&quot;') + '"' + checked + '>' +
            '<span>' + o.text + '</span>' +
          '</label>' +
        '</li>'
      );
    }).join('');
  }

  function enhance(wrap) {
    // Neutralise the slide-in on every filter group's <summary>.
    wrap.querySelectorAll('summary.mobile-facets__summary').forEach(blockMenuDrawer);
    buildSort(wrap);
  }

  function initFacetAccordion(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var wrappers = scope.querySelectorAll('.mobile-facets__wrapper');
    if (!wrappers.length && document !== scope) {
      wrappers = document.querySelectorAll('.mobile-facets__wrapper');
    }
    if (!wrappers.length) return;

    wrappers.forEach(function (wrap) {
      enhance(wrap);

      // facets.js replaces each .js-filter's innerHTML on every filter change
      // (and re-runs MenuDrawer.bindEvents), so re-apply on any DOM change.
      if (wrap.dataset.hcAccObserved === '1') return;
      wrap.dataset.hcAccObserved = '1';

      var scheduled = false;
      var observer = new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(function () {
          scheduled = false;
          observer.disconnect();
          enhance(wrap);
          observer.observe(wrap, { childList: true, subtree: true });
        });
      });
      observer.observe(wrap, { childList: true, subtree: true });
    });
  }

  document.addEventListener('DOMContentLoaded', function () { initFacetAccordion(); });
  document.addEventListener('shopify:section:load', function (e) { initFacetAccordion(e.target); });
})();

/* -------------------------------------------------------------------------- */
/* PDP media rail                                                             */
/*                                                                            */
/* The vertical dot indicator in the gutter left of the PDP media column (see  */
/* `.hc-media-rail` in base-custom.css). One dot per ROW of the gallery grid,  */
/* so a side-by-side pair of images shares a dot; the row nearest the middle   */
/* of the screen is the active one, and clicking a dot scrolls to its row.     */
/*                                                                            */
/* Rows are read from the live layout (`offsetTop`), never assumed, so the     */
/* same code covers the 2-across desktop grid, a single-column gallery, and    */
/* whatever the variant switch leaves visible. Centring is pure CSS -- this    */
/* file only decides which dot is lit.                                        */
/* -------------------------------------------------------------------------- */
(function () {
  /* Two items count as one row when their tops are within this many px. */
  var ROW_TOLERANCE = 8;
  var states = [];
  var ticking = false;
  /* Measuring is the expensive half, so it only happens when something could
     actually have moved -- scrolling alone just re-lights a dot. */
  var needsMeasure = true;

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function stickyHeaderHeight() {
    var header = document.querySelector('sticky-header');
    return header ? header.offsetHeight : 0;
  }

  /* Items still in the layout: the variant switch hides some, and a display:none
     item reports no offsetParent. */
  function visibleItems(list) {
    return Array.prototype.filter.call(
      list.querySelectorAll('.product__media-item'),
      function (item) {
        return item.offsetParent !== null && item.offsetHeight > 0;
      }
    );
  }

  function groupRows(items) {
    var rows = [];
    items.forEach(function (item, index) {
      var last = rows[rows.length - 1];
      if (last && Math.abs(last.top - item.offsetTop) <= ROW_TOLERANCE) {
        last.items.push(item);
      } else {
        /* `position` is the 1-based index of the row's first image, which is
           what the aria-label quotes -- row 2 of a 2-across grid is image 3. */
        rows.push({ top: item.offsetTop, position: index + 1, items: [item] });
      }
    });
    return rows;
  }

  /* Viewport-relative span of a row, i.e. the tallest of its images. */
  function rowBounds(row) {
    var top = Infinity;
    var bottom = -Infinity;
    row.items.forEach(function (item) {
      var rect = item.getBoundingClientRect();
      top = Math.min(top, rect.top);
      bottom = Math.max(bottom, rect.bottom);
    });
    return { top: top, bottom: bottom };
  }

  function scrollToRow(row) {
    var top = rowBounds(row).top + window.pageYOffset - stickyHeaderHeight() - 16;
    window.scrollTo({
      top: Math.max(top, 0),
      behavior: reducedMotion() ? 'auto' : 'smooth'
    });
  }

  function buildDots(state) {
    var label = state.track.dataset.dotLabel || 'Image %INDEX%';
    state.track.textContent = '';

    state.rows.forEach(function (row, index) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'hc-media-rail__dot';
      dot.setAttribute('aria-label', label.replace('%INDEX%', String(row.position)));
      dot.addEventListener('click', function () {
        /* Read the row fresh: the layout may have been remeasured since. */
        var current = state.rows[index];
        if (current) scrollToRow(current);
      });
      state.track.appendChild(dot);
    });

    state.dots = Array.prototype.slice.call(state.track.children);
  }

  /* Cheap stand-in for "did the gallery layout change?": item count, list
     height and gutter width cover a late-loading image and a resize; the first
     item's id catches a variant switch, which prepends the selected media
     without changing any of the other three. */
  function signature(state, items) {
    return [
      items.length,
      state.list.offsetHeight,
      state.rail.offsetWidth,
      items.length ? items[0].dataset.mediaId : ''
    ].join(':');
  }

  function measure(state) {
    var items = visibleItems(state.list);
    var next = signature(state, items);
    if (next === state.signature) return;
    state.signature = next;

    state.rows = groupRows(items);

    if (state.rows.length < 2) {
      state.rail.hidden = true;
      state.track.textContent = '';
      state.dots = [];
      return;
    }

    state.rail.hidden = false;
    buildDots(state);
  }

  function highlight(state) {
    if (!state.dots.length) return;

    var middle = window.innerHeight / 2;
    var active = 0;
    var closest = Infinity;

    state.rows.forEach(function (row, index) {
      var bounds = rowBounds(row);
      var distance = 0;
      if (middle < bounds.top) {
        distance = bounds.top - middle;
      } else if (middle > bounds.bottom) {
        distance = middle - bounds.bottom;
      }
      if (distance < closest) {
        closest = distance;
        active = index;
      }
    });

    if (active === state.active) return;
    state.active = active;

    state.dots.forEach(function (dot, index) {
      var isActive = index === active;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  function update() {
    ticking = false;
    var remeasure = needsMeasure;
    needsMeasure = false;

    /* Drop sections the theme editor has replaced. */
    states = states.filter(function (state) { return state.rail.isConnected; });

    states.forEach(function (state) {
      /* Below 750px the rail is display:none (`small-hide`) -- nothing to
         measure and nothing to light up. Same for a hidden quick-add copy. */
      if (state.rail.hidden !== true && state.rail.offsetParent === null) {
        state.signature = null;
        state.active = -1;
        return;
      }
      if (remeasure) measure(state);
      highlight(state);
    });
  }

  function schedule() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  function scheduleMeasure() {
    needsMeasure = true;
    schedule();
  }

  function initMediaRail(scope) {
    var root = scope || document;

    root.querySelectorAll('[data-media-rail]').forEach(function (rail) {
      if (rail.dataset.railInit) return;

      var gallery = rail.closest('media-gallery');
      var list = gallery && gallery.querySelector('.product__media-list');
      var track = rail.querySelector('[data-media-rail-track]');
      if (!list || !track) return;

      rail.dataset.railInit = 'true';

      var state = {
        rail: rail,
        list: list,
        track: track,
        rows: [],
        dots: [],
        signature: null,
        active: -1
      };
      states.push(state);

      /* Images arrive at their real height late, and the variant switch swaps
         items in and out -- both show up as a height change on the list. */
      if (window.ResizeObserver) {
        new ResizeObserver(scheduleMeasure).observe(list);
      }
    });

    scheduleMeasure();
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', scheduleMeasure);
  window.addEventListener('load', scheduleMeasure);

  if (document.readyState !== 'loading') {
    initMediaRail();
  } else {
    document.addEventListener('DOMContentLoaded', function () { initMediaRail(); });
  }

  document.addEventListener('shopify:section:load', function (e) { initMediaRail(e.target); });
})();

/* -------------------------------------------------------------------------- */
/* PDP mobile media progress bar                                              */
/*                                                                            */
/* Drives `.hc-media-progress` (see base-custom.css) from the gallery slider's */
/* own scroll position, so the fill tracks the finger instead of jumping a     */
/* slide at a time. Width is (slides passed + 1) / slides, which means the bar */
/* starts one slide wide and reaches the end on the last slide.               */
/*                                                                            */
/* Slide count is read from the live layout every measure -- the variant       */
/* switch hides media items, and a hidden item reports no width.               */
/* -------------------------------------------------------------------------- */
(function () {
  function slideCount(list) {
    return Array.prototype.filter.call(
      list.querySelectorAll('.product__media-item'),
      function (item) { return item.clientWidth > 0; }
    ).length;
  }

  function render(state) {
    var count = slideCount(state.list);
    if (count < 2) {
      state.bar.style.display = 'none';
      return;
    }
    state.bar.style.display = '';

    var max = state.list.scrollWidth - state.list.clientWidth;
    var ratio = max > 0 ? state.list.scrollLeft / max : 0;
    if (ratio < 0) ratio = 0;
    if (ratio > 1) ratio = 1;

    /* One slide's worth of bar, plus whatever of the remaining slides the
       customer has scrolled past. */
    var progress = (1 + ratio * (count - 1)) / count;
    state.fill.style.width = (progress * 100).toFixed(2) + '%';
  }

  function initMediaProgress(scope) {
    var root = scope || document;

    root.querySelectorAll('[data-media-progress]').forEach(function (bar) {
      if (bar.dataset.progressInit) return;

      var gallery = bar.closest('media-gallery');
      var list = gallery && gallery.querySelector('.product__media-list');
      var fill = bar.querySelector('[data-media-progress-fill]');
      if (!list || !fill) return;

      bar.dataset.progressInit = 'true';

      var state = { bar: bar, list: list, fill: fill };
      var ticking = false;
      var settle;

      function schedule() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          render(state);
        });
      }

      list.addEventListener('scroll', function () {
        /* Follow the finger while the swipe is live, then hand the last few
           pixels of momentum scroll back to the CSS transition. */
        bar.classList.add('is-dragging');
        clearTimeout(settle);
        settle = setTimeout(function () { bar.classList.remove('is-dragging'); }, 120);
        schedule();
      }, { passive: true });

      /* Images landing at their real size, and the variant switch swapping
         media in and out, both change what there is to scroll through. */
      if (window.ResizeObserver) {
        new ResizeObserver(schedule).observe(list);
      }

      window.addEventListener('resize', schedule);
      window.addEventListener('load', schedule);
      render(state);
    });
  }

  if (document.readyState !== 'loading') {
    initMediaProgress();
  } else {
    document.addEventListener('DOMContentLoaded', function () { initMediaProgress(); });
  }

  document.addEventListener('shopify:section:load', function (e) { initMediaProgress(e.target); });
})();

/* -------------------------------------------------------------------------- */
/* PDP "Customize?" request                                                   */
/*                                                                            */
/* The tile sits at the end of a variant row (rendered by the variant picker); */
/* this opens its popup and writes what the customer types into the block's    */
/* hidden `properties[...]` input. That input is a member of the product form  */
/* via its `form` attribute, so the message is submitted with add-to-cart as a */
/* line item property and lands on the order beside the item it belongs to.    */
/*                                                                            */
/* The overlay is moved to <body> on init. It has to be: the block renders     */
/* inside the product info column, which the reveal animation leaves carrying  */
/* an inline `transform` -- and a transformed ancestor becomes the containing  */
/* block for `position: fixed`, so the "full screen" overlay was only ever as  */
/* wide as that column, with the page scrolling underneath it. Only the        */
/* overlay travels; the hidden input stays put, keeping its place in the form. */
/*                                                                            */
/* Everything is delegated from the document because Dawn re-renders the whole */
/* variant picker on every variant change -- a listener bound to the tile      */
/* would die on the first click. The typed value survives that re-render: it   */
/* lives on the block's hidden input, which sits outside the picker.           */
/* -------------------------------------------------------------------------- */
(function () {
  'use strict';

  var OPEN_CLASS = 'pcz-open';

  /* The popup element that owns whatever was clicked -- looked up by id when
     the click came from the relocated overlay, by ancestry otherwise. */
  function pczFor(el) {
    var overlay = el.closest('[data-customize-overlay]');
    if (overlay && overlay.dataset.customizeOwner) {
      return document.getElementById(overlay.dataset.customizeOwner);
    }

    var opener = el.closest('[data-customize-open]');
    if (opener) {
      var scope = opener.closest('.shopify-section, product-info') || document;
      return scope.querySelector('product-customize');
    }

    return el.closest('product-customize');
  }

  function parts(pcz) {
    var overlay = pcz && pcz.pczOverlay;
    return {
      overlay: overlay,
      value: pcz ? pcz.querySelector('[data-customize-value]') : null,
      panel: overlay ? overlay.querySelector('[data-customize-panel]') : null,
      input: overlay ? overlay.querySelector('[data-customize-input]') : null,
      count: overlay ? overlay.querySelector('[data-customize-count]') : null,
    };
  }

  /* The tile's "filled" state is a class on the section, not on the tile: the
     tile is replaced whenever the picker re-renders, the section isn't. */
  function reflect(pcz) {
    var p = parts(pcz);
    var section = pcz.closest('.main-product-section') || pcz.closest('.shopify-section');
    if (!section || !p.value) return;
    section.classList.toggle('is-customized', p.value.value.trim() !== '');
  }

  function updateCount(pcz) {
    var p = parts(pcz);
    if (p.count && p.input) p.count.textContent = String(p.input.value.length);
  }

  function open(pcz) {
    var p = parts(pcz);
    if (!p.overlay || !p.input || !p.value) return;

    // Reopening shows what was saved, not what was half-typed and abandoned.
    p.input.value = p.value.value;
    updateCount(pcz);

    p.overlay.hidden = false;
    // Both elements: `overflow` on body only reaches the viewport while html
    // is `visible`, which no theme can be relied on to leave alone.
    document.documentElement.classList.add(OPEN_CLASS);
    p.input.focus();
  }

  function close(pcz) {
    var p = parts(pcz);
    if (!p.overlay) return;
    p.overlay.hidden = true;
    if (!document.querySelector('[data-customize-overlay]:not([hidden])')) {
      document.documentElement.classList.remove(OPEN_CLASS);
    }
  }

  function save(pcz) {
    var p = parts(pcz);
    if (!p.value || !p.input) return;
    p.value.value = p.input.value.trim();
    reflect(pcz);
    close(pcz);
  }

  function clear(pcz) {
    var p = parts(pcz);
    if (!p.value || !p.input) return;
    p.input.value = '';
    p.value.value = '';
    updateCount(pcz);
    reflect(pcz);
    close(pcz);
  }

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    if (target.closest('[data-customize-open]')) {
      e.preventDefault();
      var toOpen = pczFor(target);
      if (toOpen) open(toOpen);
      return;
    }

    if (target.closest('[data-customize-close]')) {
      close(pczFor(target));
      return;
    }

    if (target.closest('[data-customize-save]')) {
      save(pczFor(target));
      return;
    }

    if (target.closest('[data-customize-clear]')) {
      clear(pczFor(target));
      return;
    }

    // A click on the backdrop itself -- never one that bubbled from the panel.
    var overlay = target.closest('[data-customize-overlay]');
    if (overlay && target === overlay) close(pczFor(target));
  });

  document.addEventListener('input', function (e) {
    var input = e.target;
    if (!input || !input.matches || !input.matches('[data-customize-input]')) return;
    updateCount(pczFor(input));
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-customize-overlay]:not([hidden])').forEach(function (overlay) {
      close(pczFor(overlay));
    });
  });

  function initCustomize(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;

    root.querySelectorAll('product-customize').forEach(function (pcz) {
      var overlay = pcz.pczOverlay;

      if (!overlay) {
        overlay = pcz.querySelector('[data-customize-overlay]');
        if (!overlay) return;

        // A re-render in the theme editor leaves the previous copy behind.
        var stale = document.querySelector(
          '[data-customize-overlay][data-customize-owner="' + pcz.id + '"]'
        );
        if (stale) stale.remove();

        overlay.dataset.customizeOwner = pcz.id;
        document.body.appendChild(overlay);
        pcz.pczOverlay = overlay;
      }

      reflect(pcz);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initCustomize(); });
  } else {
    initCustomize();
  }

  document.addEventListener('shopify:section:load', function (e) { initCustomize(e.target); });
})();


/* -------------------------------------------------------------------------- */
/* PDP size chart popup                                                       */
/*                                                                            */
/* Same shape as the customize popup above, and for the same reasons: the      */
/* overlay is moved to <body> on init (a transformed ancestor would become the */
/* containing block for `position: fixed`), and every listener is delegated    */
/* from the document because Dawn re-renders the whole variant picker -- and   */
/* with it the trigger button -- on every variant change.                      */
/* -------------------------------------------------------------------------- */
(function () {
  'use strict';

  var OPEN_CLASS = 'psc-open';

  /* The popup that owns whatever was clicked. The trigger names its popup by
     id, so a product showing one chart and an editor preview showing another
     can never cross wires. */
  function pscFor(el) {
    var overlay = el.closest('[data-size-chart-overlay]');
    if (overlay && overlay.dataset.sizeChartOwner) {
      return document.getElementById(overlay.dataset.sizeChartOwner);
    }

    var opener = el.closest('[data-size-chart-open]');
    if (opener) {
      return document.getElementById(opener.dataset.sizeChartOpen);
    }

    return el.closest('size-chart-modal');
  }

  function open(psc) {
    if (!psc || !psc.pscOverlay) return;
    psc.pscOverlay.hidden = false;
    document.documentElement.classList.add(OPEN_CLASS);

    var panel = psc.pscOverlay.querySelector('[data-size-chart-panel]');
    if (panel) panel.focus();
  }

  function close(psc) {
    if (!psc || !psc.pscOverlay) return;
    psc.pscOverlay.hidden = true;
    if (!document.querySelector('[data-size-chart-overlay]:not([hidden])')) {
      document.documentElement.classList.remove(OPEN_CLASS);
    }
  }

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    if (target.closest('[data-size-chart-open]')) {
      e.preventDefault();
      open(pscFor(target));
      return;
    }

    if (target.closest('[data-size-chart-close]')) {
      close(pscFor(target));
      return;
    }

    // A click on the backdrop itself -- never one that bubbled from the panel.
    var overlay = target.closest('[data-size-chart-overlay]');
    if (overlay && target === overlay) close(pscFor(target));
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-size-chart-overlay]:not([hidden])').forEach(function (overlay) {
      close(pscFor(overlay));
    });
  });

  function initSizeChart(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;

    root.querySelectorAll('size-chart-modal').forEach(function (psc) {
      if (psc.pscOverlay) return;

      var overlay = psc.querySelector('[data-size-chart-overlay]');
      if (!overlay) return;

      // A re-render in the theme editor leaves the previous copy behind.
      var stale = document.querySelector(
        '[data-size-chart-overlay][data-size-chart-owner="' + psc.id + '"]'
      );
      if (stale) stale.remove();

      overlay.dataset.sizeChartOwner = psc.id;
      document.body.appendChild(overlay);
      psc.pscOverlay = overlay;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initSizeChart(); });
  } else {
    initSizeChart();
  }

  document.addEventListener('shopify:section:load', function (e) { initSizeChart(e.target); });
})();
